// src/components/IjrrGear/SingleProduct/ImageEditor.js

import React, { useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { FabricJSCanvas, useFabricJSEditor } from 'fabricjs-react';
import './ImageEditor.css';

const ImageEditor = forwardRef(({ 
    selectedImageUrl, 
    overlayImageUrl, 
    overlayAssetId, 
    onObjectSelected = () => {}, 
    onObjectDeselected = () => {}, 
    onOverlayCleared = () => {} 
}, ref) => {
    const { editor, onReady } = useFabricJSEditor();
    const snapThreshold = 10;
    const canvasRef = useRef(null);

    useImperativeHandle(ref, () => ({
        /**
         * Prepares the canvas for export by loading background and objects.
         */
        async prepareView(bgUrl, objectsArray) {
            const canvas = canvasRef.current;
            if (!canvas || !canvas.getContext()) return;

            return new Promise((resolve) => {
                const img = new window.Image();
                img.crossOrigin = "anonymous";
                img.src = bgUrl;
                img.onload = () => {
                    if (!canvas || !canvas.getContext()) return resolve();
                    const scale = Math.min(canvas.width / img.width, canvas.height / img.height) * 0.98;
                    const fabricBg = new window.fabric.Image(img);

                    canvas.setBackgroundImage(fabricBg, () => {
                        if (!canvas || !canvas.getContext()) return resolve();
                        const currentOverlays = canvas.getObjects().filter(obj => 
                            obj.type === 'image' && obj !== canvas.backgroundImage
                        );
                        currentOverlays.forEach(obj => canvas.remove(obj));

                        if (objectsArray && objectsArray.length > 0) {
                            window.fabric.util.enlivenObjects(objectsArray, (enlivenedObjects) => {
                                if (canvas && canvas.getContext()) {
                                    enlivenedObjects.forEach(obj => canvas.add(obj));
                                    canvas.renderAll();
                                }
                                resolve();
                            });
                        } else {
                            canvas.renderAll();
                            resolve();
                        }
                    }, {
                        originX: 'center', originY: 'center',
                        top: canvas.height / 2, left: canvas.width / 2,
                        scaleX: scale, scaleY: scale
                    });
                };
            });
        },

        getDesignJSON() {
            const canvas = canvasRef.current;
            if (!canvas) return [];
            return canvas.getObjects()
                .filter(obj => obj.type === 'image' && obj !== canvas.backgroundImage)
                .map(obj => obj.toObject(['assetId'])); 
        },

        loadDesignJSON(objectsArray) {
            const canvas = canvasRef.current;
            if (!canvas || !canvas.getContext()) return;
            const currentOverlays = canvas.getObjects().filter(obj => 
                obj.type === 'image' && obj !== canvas.backgroundImage
            );
            currentOverlays.forEach(obj => canvas.remove(obj));

            if (!objectsArray || objectsArray.length === 0) {
                canvas.renderAll();
                return;
            }

            window.fabric.util.enlivenObjects(objectsArray, (enlivenedObjects) => {
                if (canvas && canvas.getContext()) {
                    enlivenedObjects.forEach(obj => canvas.add(obj));
                    canvas.renderAll();
                }
            });
        },

        exportCanvasAsPNG() {
            return new Promise((resolve, reject) => {
                const canvas = canvasRef.current;
                if (!canvas || !canvas.getContext()) return reject(new Error('Canvas lost.'));
                try {
                    const dataURL = canvas.toDataURL({ format: 'png', quality: 1.0, multiplier: 2 });
                    fetch(dataURL).then(res => res.blob()).then(resolve).catch(reject);
                } catch (error) { reject(error); }
            });
        },

        addOverlayImage: (url, id) => addOverlayImage(url, id),
    }));

    const handleObjectMoving = e => {
        const object = e.target;
        if (Math.abs(object.left - editor.verticalGuideLine) < snapThreshold) {
            object.set({ left: editor.verticalGuideLine, originX: 'center' });
            showVerticalGuideLine();
        }
        if (Math.abs(object.top - editor.horizontalGuideLine) < snapThreshold) {
            object.set({ top: editor.horizontalGuideLine, originY: 'center' });
            showHorizontalGuideLine();
        }
        if (canvasRef.current && canvasRef.current.getContext()) canvasRef.current.renderAll();
    };

    const showVerticalGuideLine = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (editor.vLine) canvas.remove(editor.vLine);
        editor.vLine = new window.fabric.Line(
            [editor.verticalGuideLine, 0, editor.verticalGuideLine, canvas.getHeight()],
            { stroke: '#808080', strokeWidth: 1, strokeDashArray: [4, 3], selectable: false, evented: false }
        );
        canvas.add(editor.vLine);
    };

    const showHorizontalGuideLine = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (editor.hLine) canvas.remove(editor.hLine);
        editor.hLine = new window.fabric.Line(
            [0, editor.horizontalGuideLine, canvas.getWidth(), editor.horizontalGuideLine],
            { stroke: '#808080', strokeWidth: 1, strokeDashArray: [4, 3], selectable: false, evented: false }
        );
        canvas.add(editor.hLine);
    };

    const hideGuideLines = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (editor.vLine) { canvas.remove(editor.vLine); editor.vLine = null; }
        if (editor.hLine) { canvas.remove(editor.hLine); editor.hLine = null; }
        if (canvas.getContext()) canvas.renderAll();
    };

    const handleDeleteKey = e => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const activeObject = canvas.getActiveObject();
        if ((e.key === 'Delete' || e.key === 'Backspace') && activeObject) {
            canvas.remove(activeObject);
            canvas.renderAll();
            const remaining = canvas.getObjects().filter(o => o.type === 'image' && o !== canvas.backgroundImage);
            if (remaining.length === 0) onOverlayCleared();
        }
    };

    const addOverlayImage = (imageUrl, assetId = null) => {
        const canvas = canvasRef.current;
        if (!canvas || !imageUrl) return;

        // Protection: Ensure only one design overlay exists at a time
        const currentOverlays = canvas.getObjects().filter(obj => 
            obj.type === 'image' && obj !== canvas.backgroundImage
        );
        currentOverlays.forEach(obj => canvas.remove(obj));

        const img = new window.Image();
        img.crossOrigin = "anonymous";
        img.src = imageUrl;
        img.onload = () => {
            if (!canvas || !canvas.getContext()) return;
            const maxImgSize = 250;
            const scale = Math.min(maxImgSize / img.width, maxImgSize / img.height);
            const overlay = new window.fabric.Image(img, {
                left: canvas.width / 2, top: canvas.height / 2,
                originX: 'center', originY: 'center',
                scaleX: scale, scaleY: scale,
                cornerSize: 10, hasRotatingPoint: true,
                rotatingPointOffset: 40, borderColor: '#c5900b',
                cornerColor: '#8a2909', transparentCorners: false,
                crossOrigin: 'anonymous',
                assetId: assetId 
            });
            canvas.add(overlay).setActiveObject(overlay);
            canvas.renderAll();
        };
    };

    useEffect(() => {
        if (editor?.canvas) {
            canvasRef.current = editor.canvas;
            editor.verticalGuideLine = editor.canvas.getWidth() / 2;
            editor.horizontalGuideLine = editor.canvas.getHeight() / 2;
            editor.canvas.on('object:moving', handleObjectMoving);
            editor.canvas.on('selection:created', e => onObjectSelected(e.selected?.[0]));
            editor.canvas.on('selection:updated', e => onObjectSelected(e.selected?.[0]));
            editor.canvas.on('selection:cleared', onObjectDeselected);
            editor.canvas.on('mouse:up', hideGuideLines);
            document.addEventListener('keydown', handleDeleteKey);
            return () => {
                document.removeEventListener('keydown', handleDeleteKey);
                canvasRef.current = null;
            };
        }
    }, [editor]);

    // Reacting to Background changes
    useEffect(() => {
        if (editor && selectedImageUrl) {
            const canvas = canvasRef.current;
            const img = new window.Image();
            img.crossOrigin = "anonymous";
            img.src = selectedImageUrl;
            img.onload = () => {
                const scale = Math.min(canvas.width / img.width, canvas.height / img.height) * 0.98;
                const fabricBg = new window.fabric.Image(img);
                canvas.setBackgroundImage(fabricBg, canvas.renderAll.bind(canvas), {
                    originX: 'center', originY: 'center',
                    top: canvas.height / 2, left: canvas.width / 2,
                    scaleX: scale, scaleY: scale
                });
            };
        }
    }, [editor, selectedImageUrl]);

    // Reacting to Overlay changes (Declarative Flow)
    useEffect(() => {
        if (editor && overlayImageUrl) {
            addOverlayImage(overlayImageUrl, overlayAssetId);
        }
    }, [editor, overlayImageUrl, overlayAssetId]);

    return <FabricJSCanvas className="imageEditorCanvas" onReady={onReady} />;
});

export default ImageEditor;